import React from 'react';
import { AlertTriangle } from 'lucide-react';
import {
    GeographyOutcomeKind,
    SiteImportOutcomeKind,
    type GeographyFailure,
    type ImportResult,
    type ImportRowResult,
} from '../../hooks/useImportSites.types';

type ImportResultCalloutProps = {
    result: ImportResult;
    /** Omit when the callout is shown inline (e.g. in the wizard) where an
     *  explicit Continue button already advances the user. The dismiss
     *  affordance is for contexts (like OrgSiteManagement) where the user
     *  just wants to clear the message and carry on. */
    onDismiss?: () => void;
};

type GeographyFailureRow = {
    projectId: string;
    projectName: string;
    failure: GeographyFailure;
};

const toGeographyFailureRows = (rows: ImportRowResult[]): GeographyFailureRow[] =>
    rows.flatMap(row =>
        row.outcome.kind === SiteImportOutcomeKind.Imported && row.outcome.geography.kind === GeographyOutcomeKind.Failed
            ? [{ projectId: row.projectId, projectName: row.projectName, failure: row.outcome.geography.failure }]
            : []
    );

// Post-import summary. Neutral background with per-category tone on the
// number, so a partially-failed batch is no longer hidden behind a uniformly
// green Alert. Mirrors the PreviewSummaryCallout used in the manifest flow.
export const ImportResultCallout = ({ result, onDismiss }: ImportResultCalloutProps) => {
    const geographyFailures = toGeographyFailureRows(result.rows);

    return (
        <div data-testid='import-result-callout' className='relative mb-3 rounded border border-grey-30 bg-white p-5 shadow-sm'>
            {onDismiss && (
                <button
                    type='button'
                    className='absolute top-2 right-3 text-lg opacity-70 hover:opacity-100'
                    onClick={onDismiss}
                    aria-label='Dismiss'
                >
                    ×
                </button>
            )}
            <div className='flex flex-wrap items-baseline gap-x-10 gap-y-3'>
                <Headline
                    label='Imported'
                    value={result.imported}
                    tone={result.imported > 0 ? 'success' : 'muted'}
                    testId='import-result-imported'
                />
                <Headline
                    label='Already existed'
                    value={result.skipped}
                    tone={result.skipped > 0 ? 'info' : 'muted'}
                    testId='import-result-skipped'
                />
                <Headline
                    label='Geography failed'
                    value={result.geographyFailed}
                    tone={result.geographyFailed > 0 ? 'danger' : 'muted'}
                    testId='import-result-geography-failed'
                />
                <Headline
                    label='Failed'
                    value={result.failed}
                    tone={result.failed > 0 ? 'danger' : 'muted'}
                    testId='import-result-failed'
                />
            </div>
            {geographyFailures.length > 0 && <GeographyFailureDetails rows={geographyFailures} />}
        </div>
    );
};

const GeographyFailureDetails = ({ rows }: { rows: GeographyFailureRow[] }) => (
    <div data-testid='geography-failure-details' className='mt-4 border-t border-grey-20 pt-3'>
        <p className='mb-2 text-xs uppercase tracking-wide text-grey-70'>Geography failures</p>
        <ul className='flex flex-col gap-1.5'>
            {rows.map(row => (
                <li
                    key={row.projectId}
                    data-testid={`geography-failure-row-${row.projectId}`}
                    className='flex items-start gap-2 text-sm text-grey-100'
                >
                    <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0 text-red-100' aria-hidden='true' />
                    <span className='font-medium'>{row.projectName}</span>
                    <span className='text-grey-70'>·</span>
                    <span className='font-mono text-xs text-grey-70'>{row.failure.step}</span>
                    {row.failure.status !== undefined && (
                        <>
                            <span className='text-grey-70'>·</span>
                            <span className='font-mono text-xs text-grey-70'>{row.failure.status}</span>
                        </>
                    )}
                    {row.failure.message !== undefined && (
                        <>
                            <span className='text-grey-70'>·</span>
                            <span className='italic text-red-100'>{row.failure.message}</span>
                        </>
                    )}
                </li>
            ))}
        </ul>
    </div>
);

type Tone = 'success' | 'info' | 'danger' | 'muted';

const TONE_COLOR: Record<Tone, string> = {
    success: 'text-green-100',
    info: 'text-blue-100',
    danger: 'text-red-100',
    muted: 'text-grey-70',
};

const Headline = ({ label, value, tone, testId }: { label: string; value: number; tone: Tone; testId: string }) => (
    <div className='flex flex-col'>
        <span className='text-xs uppercase tracking-wide text-grey-70'>{label}</span>
        <span className={`text-3xl font-semibold ${TONE_COLOR[tone]}`} data-testid={testId}>
            {value}
        </span>
    </div>
);
